<template>
  <div class="tracker-list-container">
    <div class="controls">
      <input type="text" v-model="filterText" placeholder="Filter trackers..." class="filter-input" />
    </div>

    <div v-if="loading" class="loading">Loading trackers...</div>
    <div v-else-if="filteredItems.length === 0" class="empty">No trackers found.</div>
    
    <ul v-else class="tracker-list">
      <li v-for="item in filteredItems" :key="item.id" class="tracker-item" :class="{ 'item-completed': item.isCompleted }" @click="toggleExpand(item.id)">
        <div class="tracker-header">
          <h4>{{ item.itemName }}</h4>
          <div class="header-right">
            <span class="header-date" :class="{'overdue': isOverdue(item.nextDue)}" v-if="!item.isCompleted">
              {{ formatDate(item.nextDue) }}
            </span>
            <span class="header-date completed-badge" v-else>
              Completed
            </span>
            <span class="expand-icon">{{ expandedId === item.id ? '▼' : '▶' }}</span>
          </div>
        </div>
        
        <div v-if="expandedId === item.id" class="tracker-details">
          <p>
            <strong>Last Completed:</strong> {{ formatDate(item.lastCompleted) }} <br/>
            <span v-if="!item.isCompleted">
              <strong>{{ item.isRecurring ? 'Next Due' : 'Due Date' }}:</strong> <span :class="{'overdue': isOverdue(item.nextDue)}">{{ formatDate(item.nextDue) }}</span> <br/>
            </span>
            <span v-if="item.isRecurring"><strong>Recurrence:</strong> {{ item.recurrenceValue }} {{ item.recurrenceUnit }}</span>
          </p>
          <div class="tracker-actions">
            <button class="btn btn-complete" v-if="!item.isCompleted" @click.stop="completeItem(item)">Mark Completed</button>
            <div class="tracker-secondary-actions">
              <button class="btn btn-edit" @click.stop="$emit('edit', item)">Edit</button>
              <button class="btn btn-delete" @click.stop="confirmDelete(item.id)">Delete</button>
            </div>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { computed, ref, onMounted } from 'vue';
import { useStore } from 'vuex';

export default {
  name: 'TrackerList',
  emits: ['edit'],
  setup() {
    const store = useStore();
    const filterText = ref('');
    const expandedId = ref(null);

    onMounted(() => {
      store.dispatch('tracker/fetchItems');
    });

    const items = computed(() => store.getters['tracker/getItems'] || []);
    const loading = computed(() => store.getters['tracker/isLoading']);

    const filteredItems = computed(() => {
      if (!filterText.value) return items.value;
      const lowerFilter = filterText.value.toLowerCase();
      return items.value.filter(item => 
        item.itemName.toLowerCase().includes(lowerFilter)
      );
    });

    const toggleExpand = (id) => {
      expandedId.value = expandedId.value === id ? null : id;
    };

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      const d = new Date(dateString);
      return d.toLocaleDateString();
    };

    const isOverdue = (dateString) => {
      if (!dateString) return false;
      return new Date(dateString) < new Date();
    };

    const confirmDelete = (id) => {
      if (confirm('Are you sure you want to delete this tracker?')) {
        store.dispatch('tracker/removeItem', id);
      }
    };

    const completeItem = (item) => {
      store.dispatch('tracker/completeItem', item);
    };

    return {
      filterText,
      filteredItems,
      loading,
      expandedId,
      toggleExpand,
      formatDate,
      isOverdue,
      confirmDelete,
      completeItem
    };
  }
};
</script>

<style scoped lang="scss">
.tracker-list-container {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
}

.controls {
  margin-bottom: 20px;
}

.filter-input {
  width: 100%;
  padding: 10px;
  border-radius: 4px;
  border: 1px solid #ccc;
  font-size: 16px;
  box-sizing: border-box;
}

.tracker-list {
  list-style-type: none;
  padding: 0;
  margin: 0;
}

.tracker-item {
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  color: $text-color;
  margin-bottom: 12px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  text-align: left;
  cursor: pointer;
  overflow: hidden;
}

.item-completed {
  opacity: 0.7;
}

.item-completed h4 {
  text-decoration: line-through;
}

.completed-badge {
  color: #a0d8b3;
  font-style: italic;
}

.tracker-header {
  padding: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.tracker-header h4 {
  margin: 0;
}

.expand-icon {
  font-size: 12px;
  opacity: 0.7;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 15px;
}

.header-date {
  font-size: 14px;
  font-weight: bold;
}

.tracker-details {
  padding: 0 15px 15px 15px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  border-top: 1px solid rgba(255,255,255,0.05);
  margin-top: 5px;
  padding-top: 15px;
}

.tracker-item:nth-child(odd) .tracker-details {
  border-top: 1px solid rgba(255,255,255,0.05);
}

.tracker-details p {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
}

.tracker-item:nth-child(odd) {
  background: rgba(255, 255, 255, 0.03);
  color: $text-color;
}

.tracker-item:hover {
  box-shadow: 0 4px 15px rgba(6, 182, 212, 0.15);
  border-color: rgba(6, 182, 212, 0.3);
  transform: translateY(-2px);
}

.overdue {
  color: #d9534f;
  font-weight: bold;
}

.tracker-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tracker-secondary-actions {
  display: flex;
  gap: 8px;
}

.tracker-secondary-actions .btn {
  flex: 1;
}

.btn-complete {
  background-color: rgba(6, 182, 212, 0.1);
  color: #06b6d4;
  border: 1px solid rgba(6, 182, 212, 0.3);
  width: 100%;
}

.btn-complete:hover {
  background-color: rgba(6, 182, 212, 0.2);
  color: white;
  box-shadow: 0 0 10px rgba(6, 182, 212, 0.3);
}

.btn {
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
}

.btn-edit {
  background-color: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
  border: 1px solid rgba(245, 158, 11, 0.3);
}
.btn-edit:hover { 
  background-color: rgba(245, 158, 11, 0.2); 
  color: white;
  box-shadow: 0 0 10px rgba(245, 158, 11, 0.3);
}

.btn-delete {
  background-color: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.3);
}
.btn-delete:hover { 
  background-color: rgba(239, 68, 68, 0.2); 
  color: white;
  box-shadow: 0 0 10px rgba(239, 68, 68, 0.3);
}

.loading, .empty {
  padding: 20px;
  text-align: center;
  font-weight: bold;
}
</style>
